import { FlexBox } from "components/Common/FlexBox"
import { StyledSearchIngredients } from "./styled"
import SearchAutocomplete from "components/search/SearchAutocomplete"
import {
  useExternalSuggestedSearchTexts,
  useSearchExternalFoods,
  useRecentIngredients,
  useCreateRecentIngredient,
  useDeleteRecentIngredient,
} from "features/food/hooks/index"
import { useRef, useState, useEffect } from "react"
import SuggestedSearchTexts from "components/search/SuggestedSearchTexts"
import SearchFoodResults from "components/search/SearchFoodResults"
import EdamaFoodDetails from "components/foodDetails/EdamaFoodDetails"
import RecentIngredientDetails from "components/foodDetails/RecentIngredientDetails"
import {
  IEdamaFoodSearchResult,
  IEdamaIngredientOption,
} from "model/food/types/edama.types"
import { RecentIngredientType } from "model/food/types/recentIngredient.types"
import RecentIngredients from "./RecentIngredients"
import {
  getRecentIngredientPayloadFromFood,
  getRecentIngredientPayloadFromIngredientOption,
  getRecentIngredientPayloadFromRecentIngredient,
} from "model/food/utils/getPayloads/getRecentIngredientPayload"
import { useClearRecentIngredientsOnLeave } from "./hooks/useClearRecentIngredientsOnLeave"

enum IDisplayContent {
  RecentIngredients = "Recent-ingredients",
  SuggestedSearchText = "Suggested-search-text",
  SearchResults = "Search-results",
  IngredientDetails = "Ingredient-details",
  RecentIngredientDetails = "Recent-ingredient-details",
}

const SearchIngredients = ({
  setIsHideHeader,
  onAddIngredient,
}: {
  setIsHideHeader: (isHidden: boolean) => void
  onAddIngredient: (ingredient: IEdamaFoodSearchResult) => void
}) => {
  const {
    externalKeyword,
    externalSuggestedSearchTexts,
    handleExternalKeywordChange,
    isNoExternalSuggestedSearchTexts,
  } = useExternalSuggestedSearchTexts()

  const {
    searchEdamaFoodsResults,
    handleSearchExternalFoods,
    searchEdamaFoodsLoading,
    externalSearchSession,
    noSearchedExternalFoodFounded,
  } = useSearchExternalFoods()

  // Recent Ingredients hooks
  const {
    recentIngredients,
    getRecentIngredientsLoading,
    isNoRecentIngredients,
    loadMore,
    hasMore,
  } = useRecentIngredients()

  const { handleCreateRecentIngredient } = useCreateRecentIngredient()
  const { handleDeleteRecentIngredient } = useDeleteRecentIngredient()

  // Swipe state management
  const [swipedId, setSwipedId] = useState<string | null>(null)

  // Scroll position preservation
  const recentIngredientsRef = useRef<HTMLDivElement>(null)
  const searchResultRef = useRef<HTMLDivElement>(null)

  const [displayContent, setDisplayContent] = useState<IDisplayContent>(
    searchEdamaFoodsResults.length !== 0
      ? IDisplayContent.SearchResults
      : IDisplayContent.RecentIngredients,
  )

  // Clear recent ingredients when leaving to other sections (except details)
  useClearRecentIngredientsOnLeave(displayContent)

  const [ingredientDetail, setIngredientDetails] =
    useState<IEdamaFoodSearchResult | null>(null)

  const [recentIngredientDetail, setRecentIngredientDetails] =
    useState<RecentIngredientType | null>(null)

  const onSelectIngredient = (ingredient: IEdamaFoodSearchResult) => {
    setIngredientDetails(ingredient)
    setIsHideHeader(true)
    setDisplayContent(IDisplayContent.IngredientDetails)
  }

  const onSelectRecentIngredient = (ingredient: RecentIngredientType) => {
    setRecentIngredientDetails(ingredient)
    setIsHideHeader(true)
    setDisplayContent(IDisplayContent.RecentIngredientDetails)
  }

  const onDeselectFood = () => {
    setIngredientDetails(null)
    setIsHideHeader(false)
    setDisplayContent(IDisplayContent.SearchResults)
  }

  const onDeselectRecentIngredient = () => {
    setRecentIngredientDetails(null)
    setIsHideHeader(false)
    setDisplayContent(IDisplayContent.RecentIngredients)
  }

  // Handle adding ingredient from search and saving to recent
  const handleAddIngredientWithRecent = (
    ingredient: IEdamaFoodSearchResult,
  ) => {
    // Create payload for recent ingredients
    const payload = getRecentIngredientPayloadFromFood(ingredient)
    handleCreateRecentIngredient(payload)

    // Call the original callback
    onAddIngredient(ingredient)
  }

  // Handle adding ingredient from RecentIngredientDetails
  // Converts IEdamaIngredientOption to IEdamaFoodSearchResult format for onAddIngredient
  const handleAddFromRecentDetails = (
    ingredientOption: IEdamaIngredientOption,
  ) => {
    // Fire and forget - create recent ingredient in background
    const payload = getRecentIngredientPayloadFromIngredientOption(ingredientOption)
    handleCreateRecentIngredient(payload)

    // Immediately add to food (no waiting for API)
    const { foodId, uuid, ...rest } = ingredientOption
    const ingredientAsSearchResult = {
      ...rest,
      id: foodId, // Use foodId as id (parent will use this as foodId)
      uuid: uuid,
    } as IEdamaFoodSearchResult

    onAddIngredient(ingredientAsSearchResult)
  }

  // Handle quick add from recent ingredients list (without viewing details)
  const handleAddRecentIngredient = (ingredient: RecentIngredientType) => {
    // Fire and forget - create recent ingredient in background to update timestamp
    const payload = getRecentIngredientPayloadFromRecentIngredient(ingredient)
    handleCreateRecentIngredient(payload)

    // Convert RecentIngredientType to IEdamaFoodSearchResult format
    // The parent's onAddIngredient extracts `id` and uses it as `foodId`
    const { foodId, id, ...rest } = ingredient
    const ingredientAsSearchResult = {
      ...rest,
      id: foodId, // Use foodId as id (parent will use this as foodId)
      uuid: id, // Use record id as uuid
    } as IEdamaFoodSearchResult

    onAddIngredient(ingredientAsSearchResult)
  }

  if (
    displayContent === IDisplayContent.IngredientDetails &&
    ingredientDetail
  ) {
    return (
      <EdamaFoodDetails
        isIngredient
        onBack={onDeselectFood}
        food={ingredientDetail}
        onAdd={handleAddIngredientWithRecent}
        mode="add"
      />
    )
  }

  if (
    displayContent === IDisplayContent.RecentIngredientDetails &&
    recentIngredientDetail
  ) {
    return (
      <RecentIngredientDetails
        ingredient={recentIngredientDetail}
        onBack={onDeselectRecentIngredient}
        onAdd={handleAddFromRecentDetails}
      />
    )
  }

  return (
    <StyledSearchIngredients>
      <FlexBox
        style={{ padding: "0 var(--app-horizontal-padding)" }}
        $direction="column"
        $gap={16}
      >
        <SearchAutocomplete
          placeholder="Search"
          value={externalKeyword}
          onChange={(keyword) => {
            handleExternalKeywordChange(keyword)
            // Show suggestions only when there's a keyword, otherwise show recent
            if (keyword) {
              setDisplayContent(IDisplayContent.SuggestedSearchText)
            } else {
              setDisplayContent(IDisplayContent.RecentIngredients)
            }
          }}
          onClose={() => {
            handleExternalKeywordChange("")
            setDisplayContent(IDisplayContent.RecentIngredients)
          }}
          onBlur={() => {}}
          onSearch={() => {
            const forceReset = true
            handleSearchExternalFoods({
              nextKeyword: externalKeyword,
              forceReset,
            })
            setDisplayContent(IDisplayContent.SearchResults)
          }}
          visible={!!externalKeyword}
        />
      </FlexBox>
      {displayContent === IDisplayContent.RecentIngredients && (
        <RecentIngredients
          ingredients={recentIngredients}
          isNoRecentIngredients={isNoRecentIngredients}
          loadMore={loadMore}
          loading={getRecentIngredientsLoading}
          hasMore={hasMore}
          swipedId={swipedId}
          setSwipedId={setSwipedId}
          onAdd={handleAddRecentIngredient}
          onSelect={onSelectRecentIngredient}
          onDelete={handleDeleteRecentIngredient}
          contentRef={recentIngredientsRef}
        />
      )}

      {displayContent === IDisplayContent.SuggestedSearchText && (
        <div style={{ padding: "0 var(--app-horizontal-padding)" }}>
          <SuggestedSearchTexts
            isNoSuggestedSearchTexts={isNoExternalSuggestedSearchTexts}
            suggestedTexts={externalSuggestedSearchTexts}
            keyword={externalKeyword}
            onSelect={(keyword) => {
              const forceReset = true
              handleSearchExternalFoods({
                nextKeyword: keyword,
                forceReset,
              })
              setDisplayContent(IDisplayContent.SearchResults)
            }}
          />
        </div>
      )}

      {displayContent === IDisplayContent.SearchResults && (
        <div
          style={{
            height: "calc(100% - var(--input-height))",
          }}
        >
          <SearchFoodResults
            contentRef={searchResultRef}
            noSearchedFoodFounded={noSearchedExternalFoodFounded}
            foods={searchEdamaFoodsResults as IEdamaFoodSearchResult[]}
            onLoad={() =>
              handleSearchExternalFoods({ nextKeyword: externalKeyword })
            }
            loading={searchEdamaFoodsLoading}
            hasMore={!!externalSearchSession}
            keyword={externalKeyword}
            onSelectFood={(ingredient) =>
              onSelectIngredient(ingredient as IEdamaFoodSearchResult)
            }
            onAddFood={(ingredient) =>
              handleAddIngredientWithRecent(
                ingredient as IEdamaFoodSearchResult,
              )
            }
            noCreateLinkIfNoFoodFounded={true}
          />
        </div>
      )}
    </StyledSearchIngredients>
  )
}

export default SearchIngredients
